import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type {
  IComment,
  ICommentDeleteRequest,
  ICommentReaction,
  ICommentReactionRequest,
  ICommentReport,
  ICommentReportRequest,
  ICommentRequest,
  ICommentRestoreRequest,
  ICommentsResponse,
  ICommentUpdate,
  ICommentUpdateRequest,
  INewComment,
} from '@rpgc-ai/shared-interfaces/interfaces/comment.interface';

import type { Observable } from 'rxjs';

import { PUBLIC_API_REQUEST, SKIP_AUTH_ERROR_HANDLING, SKIP_GLOBAL_ERROR_NOTIFICATION } from '../auth/auth.interceptor';
import { SessionService } from '../auth/session.service';

@Injectable({
  providedIn: 'root',
})
export class ChatbotCommentsService {
  readonly #http = inject(HttpClient);
  readonly #sessionService = inject(SessionService);

  getComments(body: ICommentRequest): Observable<ICommentsResponse> {
    const { entityId, entityType, page, limit } = body;
    const context = new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATION, true);
    const requestContext =
      this.#sessionService.isAuthenticated() === false
        ? context.set(PUBLIC_API_REQUEST, true)
        : context.set(SKIP_AUTH_ERROR_HANDLING, true);

    return this.#http.get<ICommentsResponse>('/comments', {
      params: {
        entityType,
        entityId,
        page,
        limit,
      },
      context: requestContext,
    });
  }

  createNewComment(body: INewComment): Observable<IComment> {
    return this.#http.post<IComment>('/comments', body);
  }

  updateComment(body: ICommentUpdateRequest): Observable<IComment> {
    const { id, content } = body;
    const requestBody: ICommentUpdate = { content };
    return this.#http.patch<IComment>(`/comments/${id}`, requestBody);
  }

  deleteComment(body: ICommentDeleteRequest): Observable<void> {
    const { id } = body;
    return this.#http.delete<void>(`/comments/${id}`);
  }

  restoreComment(body: ICommentRestoreRequest): Observable<IComment> {
    const { id } = body;
    return this.#http.patch<IComment>(`/comments/${id}/restore`, {});
  }

  updateCommentReaction(body: ICommentReactionRequest): Observable<void> {
    const { id, reaction } = body;
    const requestBody: ICommentReaction = { reaction };
    return this.#http.put<void>(`/comments/${id}/reactions`, requestBody);
  }

  reportComment(body: ICommentReportRequest): Observable<void> {
    const { id, reason, details } = body;
    const requestBody: ICommentReport = { reason, details };
    return this.#http.post<void>(`/comments/${id}/reports`, requestBody, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATION, true),
    });
  }
}
